import { PROPERTY_TYPES_BY_NUMBER } from '~/common/constants';

export const SUBJECT_PROPERTY_OCCUPANCY_OPTIONS = [
  { value: 1, label: 'Primary Residence' },
  { value: 3, label: 'Secondary Residence' },
  { value: 2, label: 'Investment Property' },
  { value: 6, label: 'Investment - Occupy 14 Days' },
] as const;

const ALL_PROPERTY_TYPE_OPTIONS = Object.entries(PROPERTY_TYPES_BY_NUMBER).map(
  ([value, label]) => ({ value, label }),
);

const UNSUPPORTED_PROPERTY_TYPES = new Set<string>([
  'Manufactured Housing',
  'Modular',
  'Mixed-Use',
  'Mfr Purchase Home/Refi Land',
  'Manufactured Condo',
  'Manufactured PUD',
]);

export const PROPERTY_TYPE_OPTIONS = ALL_PROPERTY_TYPE_OPTIONS.filter(
  ({ label }) => !UNSUPPORTED_PROPERTY_TYPES.has(label),
);

export const PROPERTY_TYPE_OPTIONS_BY_KEYS = ALL_PROPERTY_TYPE_OPTIONS.reduce(
  (acc, { label, value }) => ({ ...acc, [label]: value }),
  {} as Record<
    (typeof ALL_PROPERTY_TYPE_OPTIONS)[number]['label'],
    (typeof ALL_PROPERTY_TYPE_OPTIONS)[number]['value']
  >,
);

export const PROPERTY_ATTACHMENT_OPTIONS = [
  { value: '99', label: 'Select an option' },
  { value: '1', label: 'Attached' },
  { value: '0', label: 'Detached' },
  { value: '2', label: 'Semi-Detached' },
] as const;

export const NUMBER_OF_UNITS_OPTIONS = [
  { value: 1, label: '1' },
  { value: 2, label: '2' },
  { value: 3, label: '3' },
  { value: 4, label: '4' },
] as const;

const HOA_DUES_ELIGIBLE_PROPERTY_TYPES = new Set([
  PROPERTY_TYPE_OPTIONS_BY_KEYS.Condominium,
  PROPERTY_TYPE_OPTIONS_BY_KEYS['Planned Unit Development'],
  PROPERTY_TYPE_OPTIONS_BY_KEYS['Site Condo'],
]);

const HOA_DUES_ELIGIBLE_OCCUPANCY_TYPES = new Set([
  PROPERTY_TYPE_OPTIONS_BY_KEYS['Single Family Residence'],
  PROPERTY_TYPE_OPTIONS_BY_KEYS['2-4 Unit Dwelling'],
]);

export const HOA_DUES_WARNING_MESSAGE =
  'Provide documentation verifying the HOA dues amount for the subject property.';

// Refinance purpose types: Refi 1st Mortgage (3), Refinance 2nd Mortgage (10), Construction Refinance (12)
export const REFINANCE_PURPOSE_TYPES = [3, 10, 12] as const;

export const BUILDING_STATUS_OPTIONS = [
  { value: 'Existing', label: 'Existing' },
  { value: 'Proposed', label: 'Proposed' },
  { value: 'Substantial Rehabilitation', label: 'Substantial Rehabilitation' },
  { value: 'Under Construction', label: 'Under Construction' },
  {
    value: 'Alterations, Improvements, or Repairs',
    label: 'Alterations, Improvements, or Repairs',
  },
  { value: 'Unknown', label: 'Unknown' },
] as const;

export const PROPERTY_REVIEW_TYPE_OPTIONS = [
  {
    value: 1,
    label:
      'Exterior Only Inspection Individual Condominium Unit Appraisal Report',
  },
  {
    value: 2,
    label:
      'Exterior Only Inspection Individual Cooperative Interest Appraisal Report',
  },
  {
    value: 3,
    label: 'Exterior Only Inspection Residential Appraisal Report',
  },
  {
    value: 4,
    label: 'Individual Condominium Unit Appraisal Report',
  },
  {
    value: 5,
    label: 'Individual Cooperative Interest Appraisal Report',
  },
  {
    value: 6,
    label: 'Manufactured Home Appraisal Report',
  },
  {
    value: 7,
    label: 'One Unit Residential Appraisal Field Review Report',
  },
  {
    value: 8,
    label: 'Small Residential Income Property Appraisal Report',
  },
  {
    value: 9,
    label: 'Two To Four Unit Residential Appraisal',
  },
  {
    value: 10,
    label: 'Uniform Residential Appraisal Report',
  },
  {
    value: 11,
    label: 'Desktop Underwriter Property Inspection Report',
  },
  {
    value: 12,
    label: 'Loan Prospector Condition And Marketability',
  },
  {
    value: 13,
    label: 'Desktop Appraisal',
  },
] as const;

export const PROPERTY_REVIEW_FORM_NUMBER_MAP: Record<number, string> = {
  1: 'FNM 1075/FRE 466',
  2: 'FNM 2095',
  3: 'FNM 2055 / FRE 2055',
  4: 'FNM 1073/FRE 465',
  5: 'FNM 2090',
  6: 'FNM 1004C/FRE 70B',
  7: 'FNM 2000 / FRE 1032',
  8: 'FNM 1025/FRE 72',
  9: 'FNM 2000A / FRE 1072',
  10: 'FNM 1004/FRE 70',
  11: 'FRE 2070',
  12: 'FRE 2070',
  13: 'FNM 1004D/FRE 70D',
};

export const CONDO_PROPERTY_TYPE_VALUES = new Set([
  '23', //Condotels
  '34', // Condominium
  '35', // High Rise Condo
  '36', // Low Rise Condo
  '39', // Mid Rise Condo
  '41', // Warrantable Condominium
  '42', // Non-Warrantable Condominium
  '48', // Site Condo
  '52', // Manufactured Condo Single Wide
  '56', // Manufactured Condo Multi Wide
]);

export const PUD_PROPERTY_TYPE_VALUES = new Set([
  PROPERTY_TYPE_OPTIONS_BY_KEYS['Planned Unit Development'],
  PROPERTY_TYPE_OPTIONS_BY_KEYS['PUD Purchase Home/Refi Land'],
  PROPERTY_TYPE_OPTIONS_BY_KEYS['Manufactured PUD'],
  PROPERTY_TYPE_OPTIONS_BY_KEYS['Manufactured PUD Single Wide'],
  PROPERTY_TYPE_OPTIONS_BY_KEYS['Manufactured PUD Multi Wide'],
]);

export const MANUFACTURED_PUD_VALUES = new Set([
  PROPERTY_TYPE_OPTIONS_BY_KEYS['Manufactured PUD Single Wide'],
  PROPERTY_TYPE_OPTIONS_BY_KEYS['Manufactured PUD Multi Wide'],
]);

export const MANUFACTURED_PROPERTY_TYPE_VALUES = new Set([
  PROPERTY_TYPE_OPTIONS_BY_KEYS['Manufactured Single Wide'],
  PROPERTY_TYPE_OPTIONS_BY_KEYS.Panelized,
  PROPERTY_TYPE_OPTIONS_BY_KEYS.Prefab,
  PROPERTY_TYPE_OPTIONS_BY_KEYS['Manufactured Multi Wide'],
  PROPERTY_TYPE_OPTIONS_BY_KEYS['Manufactured Condo Multi Wide'],
  PROPERTY_TYPE_OPTIONS_BY_KEYS['Manufactured Condo Single Wide'],
  PROPERTY_TYPE_OPTIONS_BY_KEYS['Manufactured PUD Single Wide'],
  PROPERTY_TYPE_OPTIONS_BY_KEYS['Manufactured PUD Multi Wide'],
]);

export const CERTIFICATE_OF_TITLE_TYPE_OPTIONS = [
  {
    value: '',
    label: '  ',
  },
  {
    value: 'Home Is Not Covered or Certificate is Attached',
    label: 'Home Is Not Covered or Certificate is Attached',
  },
  {
    value: 'Home Is Not Covered or Not Able To Produce Certificate',
    label: 'Home Is Not Covered or Not Able To Produce Certificate',
  },
  {
    value: 'Home Shall Be Covered by Certificate of Title',
    label: 'Home Shall Be Covered by Certificate of Title',
  },
  {
    value: 'Manufacturers Certificate Has Been Eliminated',
    label: 'Manufacturers Certificate Has Been Eliminated',
  },
  {
    value: 'Manufacturers Certificate Shall Be Eliminated',
    label: 'Manufacturers Certificate Shall Be Eliminated',
  },
  {
    value: 'Title Certificate Has Been Eliminated',
    label: 'Title Certificate Has Been Eliminated',
  },
  {
    value: 'Title Certificate Shall Be Eliminated',
    label: 'Title Certificate Shall Be Eliminated',
  },
  {
    value: 'Other',
    label: 'Other',
  },
] as const;

export const YES_NO_OPTIONS = [
  { value: '1', label: 'Yes' },
  { value: '0', label: 'No' },
] as const;

export const PROJECT_DESIGN_TYPE_OPTIONS = [
  { value: 0, label: 'None' },
  { value: 1, label: 'Garden Project' },
  { value: 2, label: 'Highrise Project' },
  { value: 3, label: 'Midrise Project' },
  { value: 5, label: 'Other' },
  { value: 4, label: 'Townhouse/Rowhouse' },
] as const;

export const FREDDIE_MAC_CLASS_OPTIONS = [
  { value: 0, label: 'None' },
  { value: 1, label: 'Streamlined Review' },
  { value: 2, label: 'Established Review' },
  { value: 3, label: 'New Project' },
  { value: 4, label: 'Detached Project' },
  { value: 5, label: '2- to 4- Unit Project' },
  { value: 6, label: 'PCondominium' },
  { value: 7, label: 'Reciprocal Review' },
  { value: 8, label: 'Exempt From Review' },
] as const;

export const FANNIE_MAE_CLASS_OPTIONS = [
  { value: 0, label: 'None' },
  { value: 1, label: 'P/Limited Review New' },
  { value: 2, label: 'Q/Limited Review Est.' },
  { value: 3, label: 'R/Expedited New' },
  { value: 4, label: 'S/Expedited Est.' },
  { value: 5, label: 'T/Fannie Mae Review' },
  { value: 6, label: 'U/FHA-approved' },
  { value: 7, label: 'V/Refi Plus' },
  { value: 8, label: 'G/not in a project or development' },
] as const;

export const PUD_CLASS_OPTIONS = [
  { value: 0, label: 'None' },
  { value: 1, label: 'E PUD' },
  { value: 2, label: 'F PUD' },
] as const;

export const isHoaConditionHidden = (
  occupancyType: string,
  isVerifiedByUnderwriter: boolean,
  hasInspectionWaiver: boolean,
  hoaDues: number,
) => {
  if (isVerifiedByUnderwriter || !hoaDues) return true;

  const isEligiblePropertyType =
    HOA_DUES_ELIGIBLE_PROPERTY_TYPES.has(occupancyType);
  const isEligibleOccupancyType =
    HOA_DUES_ELIGIBLE_OCCUPANCY_TYPES.has(occupancyType);

  return !(
    (isEligiblePropertyType && hasInspectionWaiver) ||
    isEligibleOccupancyType
  );
};
